import {
	config,
	CoveoConfigContextProvider,
	createCollateralLibraryRoutes,
	createLegalRoutes,
	createNewsRoutes,
	createProjectRoutes,
	createResourceRoutes,
	createSearchContext,
	createSearchRoute,
	EnvironmentContextProvider,
	LayoutProvider,
	ResourceContextImpl,
	ResourceContextProvider,
	SearchContextProvider,
	ThemeProvider,
	useLayoutContext,
	type CoveoConfigContextValue,
	type CreateSearchContextParams,
	type LayoutNavComponent,
	type MarketingSite,
} from '@ds-mgnl/marketing';
import type { LinkSourceProps, LinkSourceRecord } from '@dsx/react';
import { Provider as DsxProvider } from '@dsx/react';
import { forwardRef, useCallback } from 'react';
import {
	createBrowserRouter,
	Outlet,
	Link as RouterLink,
	RouterProvider,
} from 'react-router-dom';
import styles from './App.module.css';
import { Footer } from './components/Footer';
import { TopNav } from './components/TopNav';
import { useScrollManagement } from './hooks/useScrollManagement';
import { useUtmFormTracking } from './hooks/useUtmFormTracking';
import { ErrorPage } from './pages/ErrorPage/ErrorPage';
import { MagnoliaPage } from './pages/MagnoliaPage';
import { magnoliaPageLoader } from './pages/magnoliaPageLoader';
import { ProductPage } from './pages/ProductPage';
import { getMagnoliaBaseDamUrl, getMagnoliaBaseUrl } from './util';

const linkSources = {
	default: forwardRef<HTMLAnchorElement, LinkSourceProps>((props, ref) => (
		<RouterLink ref={ref} {...props} to={props.href || ''} />
	)),
	external: forwardRef<HTMLAnchorElement, LinkSourceProps>((props, ref) => (
		<a ref={ref} {...props} />
	)),
	none: forwardRef<HTMLSpanElement, LinkSourceProps>((props, ref) => (
		<span ref={ref} {...props} />
	)),
} as LinkSourceRecord;

const SHOW_ON_SITE: MarketingSite = 'maxwellThomas';
const mgnlBaseUrl = getMagnoliaBaseUrl() ?? '';
const mgnlBaseDamUrl = getMagnoliaBaseDamUrl() ?? '';

const coveoConfig: CoveoConfigContextValue = {
	commerceTrackingId:
		process.env.COVEO_COMMERCE_TRACKING_ID ?? 'maxwell-thomas',
	commerceLanguage: process.env.COVEO_COMMERCE_LANGUAGE ?? 'en',
	commerceCountry: process.env.COVEO_COMMERCE_COUNTRY ?? 'US',
	commerceCurrency:
		process.env.COVEO_COMMERCE_CURRENCY ??
		('USD' as CreateSearchContextParams['commerceCurrency']),
	contentProxyBaseUrl: process.env.COVEO_CONTENT_PROXY_BASE_URL ?? '',
	commerceProxyBaseUrl: process.env.COVEO_COMMERCE_PROXY_BASE_URL ?? '',
	analyticsProxyBaseUrl: process.env.COVEO_ANALYTICS_PROXY_BASE_URL ?? '',
};

const searchContext = createSearchContext({
	...coveoConfig,
	commerceCurrency:
		coveoConfig.commerceCurrency as CreateSearchContextParams['commerceCurrency'],
	contentSearchHub: 'MaxwellThomasContentSearch',
});

const router = createBrowserRouter([
	{
		element: <RootLayout />,
		children: [
			...createSearchRoute({
				errorElement: <ErrorPage />,
				searchHomeUrlPath: '/search',
				tabs: ['products'],
			}),
			...createResourceRoutes({
				errorElement: <ErrorPage />,
				mgnlBaseUrl,
				resourcesHomeUrlPath: '/resources',
				pageConfig: config,
				showOnSite: SHOW_ON_SITE,
			}),
			...createLegalRoutes({
				errorElement: <ErrorPage />,
				mgnlBaseUrl,
				legalHomeUrlPath: '/legal',
				pageConfig: config,
				showOnSite: SHOW_ON_SITE,
			}),
			...createCollateralLibraryRoutes({
				errorElement: <ErrorPage />,
				collateralLibUrlPath: '/collateral-library',
				mgnlBaseUrl,
				mgnlBaseDamUrl,
				mgnlCollateralHomePath: '/collateral-library',
				pageConfig: config,
				siteTitleSuffix: 'Direct Supply',
				showOnSite: SHOW_ON_SITE,
			}),
			...createNewsRoutes({
				newsHomeUrlPath: '/news',
				mgnlBaseUrl,
				pageConfig: config,
				errorElement: <ErrorPage />,
				showOnSite: SHOW_ON_SITE,
			}),
			...createProjectRoutes({
				errorElement: <ErrorPage />,
				mgnlBaseUrl,
				projectsHomeUrlPath: '/projects',
				pageConfig: config,
				showOnSite: SHOW_ON_SITE,
			}),
			{
				path: '/product/:productId',
				element: <ProductPage />,
				errorElement: <ErrorPage />,
			},
			{
				path: '*',
				element: <MagnoliaPage />,
				loader: magnoliaPageLoader,
				errorElement: <ErrorPage />,
			},
		],
	},
]);

const resourceContext = new ResourceContextImpl('/resources', SHOW_ON_SITE);

export function App() {
	useUtmFormTracking();
	const navigationComponent = useCallback<LayoutNavComponent>(
		({ transparent }) => <TopNav transparent={transparent} />,
		[],
	);

	return (
		<DsxProvider locale="en-US" linkSources={linkSources}>
			<ThemeProvider theme="maxwellThomas">
				<CoveoConfigContextProvider value={coveoConfig}>
					<SearchContextProvider value={searchContext}>
						<ResourceContextProvider value={resourceContext}>
							<EnvironmentContextProvider
								value={{
									damHost: mgnlBaseDamUrl,
									appHost: mgnlBaseUrl,
									isInMagnolia: false,
								}}
							>
								<LayoutProvider
									initialNavigationComponent={navigationComponent}
								>
									<RouterProvider router={router} />
								</LayoutProvider>
							</EnvironmentContextProvider>
						</ResourceContextProvider>
					</SearchContextProvider>
				</CoveoConfigContextProvider>
			</ThemeProvider>
		</DsxProvider>
	);
}

function RootLayout() {
	useScrollManagement();
	const { renderNavigation } = useLayoutContext();

	return (
		<div className={styles['site-container']}>
			{renderNavigation({
				transparencyOverlapRatio: 1,
				transparencyOverlapType: 'vertical',
			})}
			<main>
				<Outlet />
			</main>
			<Footer />
		</div>
	);
}
